// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Crew Training Manager
 * 
 * Manages crew training enrollment, progress tracking, and completion
 */

import { TRAINING_PROGRAMS, TRAINING_STATUS } from '../../config/CrewTrainingConfig.js';
import CrewManager from './CrewManager.js';
import gameTimeManager from './GameTimeManager.js';
import STORAGE_KEYS from '../system/STORAGE_KEYS';
import EventBus, { EVENT_TYPES } from '../system/EventBus';

class CrewTrainingManager {
  constructor() {
    // Active training sessions
    this.activeTraining = new Map(); // crewId -> training session
    
    // Training history
    this.completedTraining = new Map(); // crewId -> array of completed programs
    
    // Load from localStorage
    this.loadFromStorage();
    
  }

  /**
   * Check if crew member can enroll in training program
   */
  canEnrollInTraining(crewId, programId) {
    const crew = CrewManager.getCrewMember(crewId);
    const program = TRAINING_PROGRAMS[programId.toUpperCase()];
    
    if (!crew || !program) {
      return { canEnroll: false, reason: 'Invalid crew member or program' };
    }

    // Check if already in training
    if (this.activeTraining.has(crewId)) {
      return { canEnroll: false, reason: 'Already enrolled in training' };
    }

    // Check if injured
    if (crew.injured) {
      return { canEnroll: false, reason: 'Crew member is injured' };
    }

    // Check experience requirements
    const experienceLevels = ['ROOKIE', 'TRAINED', 'EXPERIENCED', 'VETERAN', 'ELITE'];
    const crewLevel = experienceLevels.indexOf(crew.experience);
    const requiredLevel = experienceLevels.indexOf(program.requirements.minExperience);
    
    if (crewLevel < requiredLevel) {
      return { canEnroll: false, reason: `Requires ${program.requirements.minExperience} experience` };
    }

    // Check role compatibility
    if (!program.requirements.roles.includes(crew.role)) {
      return { canEnroll: false, reason: 'Role not compatible with this training' };
    }

    // Check if already completed (can retake but with diminished returns)
    const completed = this.getCompletedTraining(crewId);
    const alreadyCompleted = completed.some(t => t.programId === programId);
    
    return { 
      canEnroll: true, 
      alreadyCompleted,
      cost: program.cost,
      duration: program.duration
    };
  }

  /**
   * Enroll crew member in training program
   */
  enrollInTraining(crewId, programId, creditsContext) {
    const eligibility = this.canEnrollInTraining(crewId, programId);
    
    if (!eligibility.canEnroll) {
      return {
        success: false,
        message: eligibility.reason
      };
    }

    const program = TRAINING_PROGRAMS[programId.toUpperCase()];
    const crew = CrewManager.getCrewMember(crewId);
    
    // Check if can afford
    if (!creditsContext.canAfford(program.cost)) {
      return {
        success: false,
        message: `Insufficient credits (need ${program.cost})`
      };
    }

    // Deduct credits
    creditsContext.removeCredits(program.cost, `${program.name} for ${crew.name}`);

    // Create training session
    const session = {
      crewId,
      programId: programId.toLowerCase(),
      program,
      startTime: gameTimeManager.getGameTime(),
      endTime: gameTimeManager.getGameTime() + (program.duration * 86400), // Convert days to seconds
      progress: 0,
      status: TRAINING_STATUS.IN_PROGRESS
    };

    this.activeTraining.set(crewId, session);
    this.saveToStorage();

    return {
      success: true,
      message: `${crew.name} enrolled in ${program.name}`,
      session
    };
  }

  /**
   * Update training progress
   */
  updateTrainingProgress() {
    const currentTime = gameTimeManager.getGameTime();
    const completedSessions = [];

    for (const [crewId, session] of this.activeTraining.entries()) {
      // Calculate progress
      const totalDuration = session.endTime - session.startTime;
      const elapsed = currentTime - session.startTime;
      session.progress = Math.min(1.0, elapsed / totalDuration);

      // Check if completed
      if (currentTime >= session.endTime) {
        this.completeTraining(crewId, session);
        completedSessions.push(session);
      }
    }

    if (completedSessions.length > 0) {
      this.saveToStorage();
    }

    return completedSessions;
  }

  /**
   * Complete training session
   */
  completeTraining(crewId, session) {
    const crew = CrewManager.getCrewMember(crewId);
    
    if (!crew) {
      this.activeTraining.delete(crewId);
      return;
    }

    // Award XP
    const leveledUp = CrewManager.awardExperience(crewId, session.program.rewards.xp);

    // Apply skill bonuses (store in crew member)
    if (!crew.skillBonuses) {
      crew.skillBonuses = {};
    }

    // Add or stack bonuses
    Object.entries(session.program.rewards.skillBonuses).forEach(([skill, bonus]) => {
      crew.skillBonuses[skill] = (crew.skillBonuses[skill] || 0) + bonus;
    });

    // Record completion
    if (!this.completedTraining.has(crewId)) {
      this.completedTraining.set(crewId, []);
    }
    
    this.completedTraining.get(crewId).push({
      programId: session.programId,
      completedAt: gameTimeManager.getGameTime(),
      xpAwarded: session.program.rewards.xp,
      bonusesAwarded: session.program.rewards.skillBonuses
    });

    // Remove from active training
    this.activeTraining.delete(crewId);

    // Dispatch completion event
    EventBus.emit(EVENT_TYPES.TRAINING_COMPLETED, {
      crewName: crew.name,
      programName: session.program.name,
      leveledUp: leveledUp.length > 0 ? leveledUp[0] : null
    });

  }

  /**
   * Cancel training (with partial refund)
   */
  cancelTraining(crewId, creditsContext) {
    const session = this.activeTraining.get(crewId);
    
    if (!session) {
      return {
        success: false,
        message: 'No active training to cancel'
      };
    }

    const crew = CrewManager.getCrewMember(crewId);
    
    // Calculate refund (50% of remaining time)
    const refundAmount = Math.floor(session.program.cost * (1 - session.progress) * 0.5);
    
    if (refundAmount > 0) {
      creditsContext.addCredits(refundAmount, `Training cancellation refund for ${crew.name}`);
    }

    this.activeTraining.delete(crewId);
    this.saveToStorage();

    return {
      success: true,
      message: `Cancelled ${session.program.name} for ${crew.name}`,
      refund: refundAmount
    };
  }

  /**
   * Get active training for crew member
   */
  getActiveTraining(crewId) {
    return this.activeTraining.get(crewId) || null;
  }

  /**
   * Get all active training sessions
   */
  getAllActiveTraining() {
    return Array.from(this.activeTraining.values());
  }

  /**
   * Get completed training for crew member
   */
  getCompletedTraining(crewId) {
    return this.completedTraining.get(crewId) || [];
  }

  /**
   * Get available programs for crew member
   */
  getAvailablePrograms(crewId) {
    const crew = CrewManager.getCrewMember(crewId);
    if (!crew) return [];

    return Object.values(TRAINING_PROGRAMS).map(program => {
      const eligibility = this.canEnrollInTraining(crewId, program.id);
      return {
        ...program,
        status: eligibility.canEnroll ? TRAINING_STATUS.AVAILABLE : TRAINING_STATUS.LOCKED,
        reason: eligibility.reason,
        alreadyCompleted: eligibility.alreadyCompleted
      };
    });
  }

  /**
   * Get training statistics
   */
  getTrainingStats() {
    const totalActive = this.activeTraining.size;
    const totalCompleted = Array.from(this.completedTraining.values())
      .reduce((sum, completions) => sum + completions.length, 0);

    const programStats = {};
    Object.keys(TRAINING_PROGRAMS).forEach(programId => {
      programStats[programId] = {
        active: 0,
        completed: 0
      };
    });

    // Count active
    for (const session of this.activeTraining.values()) {
      programStats[session.programId.toUpperCase()].active++;
    }

    // Count completed
    for (const completions of this.completedTraining.values()) {
      completions.forEach(completion => {
        programStats[completion.programId.toUpperCase()].completed++;
      });
    }

    return {
      totalActive,
      totalCompleted,
      programStats
    };
  }

  /**
   * Reset all training data
   */
  reset() {
    this.activeTraining.clear();
    this.completedTraining.clear();
    this.saveToStorage();
  }

  /**
   * Save to localStorage
   */
  saveToStorage() {
    try {
      const data = {
        activeTraining: Array.from(this.activeTraining.entries()),
        completedTraining: Array.from(this.completedTraining.entries())
      };
      localStorage.setItem(STORAGE_KEYS.CREW_TRAINING, JSON.stringify(data));
    } catch (error) {
      console.error('[CrewTrainingManager] Failed to save to localStorage:', error);
    }
  }

  /**
   * Load from localStorage
   */
  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CREW_TRAINING);
      if (data) {
        const parsed = JSON.parse(data);
        this.activeTraining = new Map(parsed.activeTraining || []);
        this.completedTraining = new Map(parsed.completedTraining || []);
      }
    } catch (error) {
      console.error('[CrewTrainingManager] Failed to load from localStorage:', error);
    }
  }
}

// Singleton instance
const crewTrainingManager = new CrewTrainingManager();
export default crewTrainingManager;